import { z } from 'zod';
import { AskSourceSchema } from './ask';

/**
 * Research memos (M5, ADR-024): an Ask Buakei answer saved into a Professional workspace,
 * edited by a lawyer, with every version kept and an explicit lawyer review. The sources are
 * a snapshot of the answer's verified citations, copied by the server from the author's own
 * chat, never supplied by the browser.
 */

export const MemoVisibilitySchema = z.enum(['private', 'workspace']);
export type MemoVisibility = z.infer<typeof MemoVisibilitySchema>;

export const MemoVersionSchema = z.object({
  version: z.number().int().positive(),
  body: z.string(),
  kind: z.enum(['ai_draft', 'edit']),
  authorUid: z.string(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
});
export type MemoVersion = z.infer<typeof MemoVersionSchema>;

export const MemoReviewSchema = z.object({
  version: z.number().int().positive(),
  reviewerUid: z.string(),
  reviewerName: z.string().nullable(),
  reviewedAt: z.string(),
});

export const MemoSummarySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  title: z.string(),
  authorUid: z.string(),
  authorName: z.string().nullable(),
  visibility: MemoVisibilitySchema,
  currentVersion: z.number().int().positive(),
  /** The lawyer review, if any; it covers only the version it names. */
  review: MemoReviewSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MemoSummary = z.infer<typeof MemoSummarySchema>;

export const MemoSchema = MemoSummarySchema.extend({
  question: z.string(),
  sources: z.array(AskSourceSchema),
  model: z.string(),
  versions: z.array(MemoVersionSchema),
  /** What the caller may do; the server checks again on every change. */
  canEdit: z.boolean(),
});
export type Memo = z.infer<typeof MemoSchema>;

export const MemoListSchema = z.object({ memos: z.array(MemoSummarySchema) });
export type MemoList = z.infer<typeof MemoListSchema>;

export const MEMO_BODY_MAX = 50_000;
export const MEMO_VERSION_LIMIT = 100;

export const CreateMemoRequestSchema = z
  .object({
    chatId: z.string().regex(/^chat_[A-Za-z0-9_-]{8,64}$/),
    /** Which answer in the chat, counting from 0. */
    turn: z.number().int().min(0).max(100),
    title: z.string().trim().min(3).max(200),
    visibility: MemoVisibilitySchema.default('private'),
  })
  .strict();
export type CreateMemoRequest = z.input<typeof CreateMemoRequestSchema>;

export const UpdateMemoRequestSchema = z
  .object({
    body: z.string().trim().min(1).max(MEMO_BODY_MAX),
    expectedVersion: z.number().int().positive(),
  })
  .strict();

export const ReviewMemoRequestSchema = z.object({ version: z.number().int().positive() }).strict();

export const MemoVisibilityRequestSchema = z.object({ visibility: MemoVisibilitySchema }).strict();

/** Export (ADR-025): a Word file, or a record that a print/PDF copy was made in the browser. */
export const ExportMemoRequestSchema = z
  .object({ format: z.enum(['docx', 'print']), version: z.number().int().positive() })
  .strict();
export type ExportMemoRequest = z.infer<typeof ExportMemoRequestSchema>;
